import React, { useState, useEffect, useMemo } from 'react';
import {
  ClipboardList, Search, Plus, Eye, X, Users, MapPin,
  Calendar, Building2, Briefcase, CheckCircle, Clock, AlertCircle,
  ChevronLeft, ChevronRight, ArrowUp, ArrowDown, RefreshCw,
  FolderKanban, Send
} from 'lucide-react';
import api from '../../services/apiService';
import SuspendedCompanyOverlay from './shared/SuspendedCompanyOverlay';

const API_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
};

const WindCompanyWorkPermits = () => {
  // Data states
  const [loading, setLoading] = useState(true);
  const [workPermits, setWorkPermits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectTechnicians, setProjectTechnicians] = useState([]);
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);
  const [workScopes, setWorkScopes] = useState([]);
  const [loadingWorkScopes, setLoadingWorkScopes] = useState(false);

  // Create modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState(new Set());
  const [formData, setFormData] = useState({
    project: '',
    turbine_name: '',
    work_scope: '',
    work_scope_category: '',
    work_description: '',
    supervisor: '',
    preparer: '',
    planned_start: '',
    planned_end: ''
  });

  // Filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'planned_end', direction: 'desc' });

  // Status configuration
  const statusConfig = {
    draft: { label: 'Draft', bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
    pending: { label: 'Pending Review', bg: 'bg-amber-50', text: 'text-amber-600', dot: 'bg-amber-500' },
    approved: { label: 'Approved', bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-500' },
    rejected: { label: 'Rejected', bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-500' },
    in_progress: { label: 'In Progress', bg: 'bg-blue-50', text: 'text-blue-600', dot: 'bg-blue-500' },
    completed: { label: 'Completed', bg: 'bg-violet-50', text: 'text-violet-600', dot: 'bg-violet-500' },
    cancelled: { label: 'Cancelled', bg: 'bg-slate-100', text: 'text-slate-500', dot: 'bg-slate-400' },
    expired: { label: 'Expired', bg: 'bg-slate-100', text: 'text-slate-500', dot: 'bg-slate-400' }
  };

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch OEM projects
        const projectsResponse = await api.get('/oem/projects/');
        setProjects(projectsResponse.data || []);

        // Fetch OEM work permits (new endpoint)
        const workPermitsResponse = await api.get('/oem/work-permits/');
        setWorkPermits(Array.isArray(workPermitsResponse.data) ? workPermitsResponse.data : []);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch technicians when project changes
  const fetchProjectTechnicians = async (projectId) => {
    if (!projectId) {
      setProjectTechnicians([]);
      return;
    }
    setLoadingTechnicians(true);
    try {
      // Get all technicians for this project (includes Resource Board planned, nominated, authorized)
      const response = await api.get(`/oem/projects/${projectId}/all-technicians/`);
      const technicians = response.data?.technicians || [];
      // Format for our use
      const formatted = technicians.map(t => ({
        id: t.id,
        name: t.full_name || `${t.first_name} ${t.last_name}`.trim() || t.username,
        profile_picture: t.profile_picture,
        authorization_level_code: t.authorization?.level_code || 'N/A',
        authorization_level_color: t.authorization?.level_color || '#94a3b8',
        has_authorization: !!t.authorization,
        authorization_valid_from: t.authorization?.valid_from,
        authorization_valid_until: t.authorization?.valid_until,
        company_name: t.company_name
      }));
      setProjectTechnicians(formatted);
    } catch (err) {
      console.error('Error fetching project technicians:', err);
      setProjectTechnicians([]);
    } finally {
      setLoadingTechnicians(false);
    }
  };

  // Fetch work scopes when project changes
  const fetchWorkScopes = async (projectId) => {
    if (!projectId) {
      setWorkScopes([]);
      return;
    }
    setLoadingWorkScopes(true);
    try {
      const response = await api.get(`/oem/work-scopes/for_project/?project_id=${projectId}`);
      setWorkScopes(response.data || []);
    } catch (err) {
      console.error('Error fetching work scopes:', err);
      setWorkScopes([]);
    } finally {
      setLoadingWorkScopes(false);
    }
  };

  // Format date/time
  const formatDateTime = (dateTime) => {
    if (!dateTime) return '-';
    return new Date(dateTime).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get team leader name
  const getTeamLeaderName = (workPermit) => {
    if (workPermit.team_data?.team_leader_name) {
      return workPermit.team_data.team_leader_name;
    }
    return '-';
  };

  // Open create modal
  const openCreateModal = () => {
    setShowCreateModal(true);
    setFormData({
      project: '',
      turbine_name: '',
      work_scope: '',
      work_description: '',
      supervisor: '',
      preparer: '',
      planned_start: '',
      planned_end: '',
      teamSearch: ''
    });
    setSelectedTeamMembers(new Set());
  };

  // Toggle team member selection
  const toggleTeamMember = (techId) => {
    setSelectedTeamMembers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(techId)) {
        newSet.delete(techId);
      } else {
        newSet.add(techId);
      }
      return newSet;
    });
  };

  // Submit work permit
  const handleSubmit = async () => {
    const { project, turbine_name, work_scope, supervisor, planned_start, planned_end } = formData;

    if (!project || !work_scope || !supervisor || !planned_start || !planned_end) {
      alert('Please fill in all required fields');
      return;
    }

    setCreateLoading(true);

    try {
      // Create work permit using new OEM endpoint
      const workPermitData = {
        project: parseInt(project),
        turbine_name,
        work_scope: parseInt(work_scope),
        work_scope_category: formData.work_scope_category ? parseInt(formData.work_scope_category) : null,
        work_description: formData.work_description,
        team_leader: parseInt(supervisor),
        preparer: formData.preparer ? parseInt(formData.preparer) : null,
        team_members: Array.from(selectedTeamMembers),
        planned_start: new Date(planned_start).toISOString(),
        planned_end: new Date(planned_end).toISOString()
      };

      await api.post('/oem/work-permits/', workPermitData);

      // Refresh work permits list
      const workPermitsResponse = await api.get('/oem/work-permits/');
      setWorkPermits(Array.isArray(workPermitsResponse.data) ? workPermitsResponse.data : []);

      setShowCreateModal(false);
      alert('Work Permit created successfully!');
    } catch (err) {
      console.error('Error creating work permit:', err);
      alert(err.response?.data?.detail || err.response?.data?.error || 'Failed to create work permit');
    } finally {
      setCreateLoading(false);
    }
  };

  // Filter work permits
  const filteredWorkPermits = useMemo(() => {
    let filtered = workPermits;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(wp => wp.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(wp => {
        const teamLeaderName = getTeamLeaderName(wp).toLowerCase();
        return (
          (wp.work_permit_number || '').toLowerCase().includes(term) ||
          (wp.turbine_name || '').toLowerCase().includes(term) ||
          (wp.work_scope_name || '').toLowerCase().includes(term) ||
          (wp.project_code || '').toLowerCase().includes(term) ||
          teamLeaderName.includes(term)
        );
      });
    }

    return filtered;
  }, [workPermits, statusFilter, searchTerm]);

  // Sort work permits
  const sortedWorkPermits = useMemo(() => {
    const sorted = [...filteredWorkPermits];

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'work_permit_number':
          aValue = a.work_permit_number || '';
          bValue = b.work_permit_number || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'planned_end':
          aValue = a.planned_end ? new Date(a.planned_end) : null;
          bValue = b.planned_end ? new Date(b.planned_end) : null;
          if (!aValue && !bValue) return 0;
          if (!aValue) return 1;
          if (!bValue) return -1;
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        default:
          aValue = '';
          bValue = '';
      }

      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });

    return sorted;
  }, [filteredWorkPermits, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedWorkPermits.length / itemsPerPage) || 1;
  const paginatedWorkPermits = sortedWorkPermits.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getVisiblePages = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1);
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  // Get status counts
  const statusCounts = useMemo(() => {
    const counts = { all: workPermits.length };
    Object.keys(statusConfig).forEach(status => {
      counts[status] = workPermits.filter(wp => wp.status === status).length;
    });
    return counts;
  }, [workPermits]);

  if (loading) {
    return (
      <SuspendedCompanyOverlay>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm border border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-slate-600 text-sm font-medium">Loading work permits...</span>
            </div>
          </div>
        </div>
      </SuspendedCompanyOverlay>
    );
  }

  return (
    <SuspendedCompanyOverlay>
      <div className="p-6 max-w-7xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-lg font-black text-slate-900 uppercase tracking-tight">Work Permits</h1>
              <p className="text-[10px] text-slate-500 font-medium">Create and manage work permit requests for OEM projects</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.location.reload()}
              className="h-9 px-3 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 flex items-center gap-1.5 font-black uppercase tracking-wide text-[10px] transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
            <button
              onClick={openCreateModal}
              disabled={projects.length === 0}
              className="h-9 px-4 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 font-black uppercase tracking-wide text-[10px] transition-colors shadow-lg shadow-emerald-500/20"
            >
              <Plus className="h-3.5 w-3.5" />
              Create Work Permit
            </button>
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-1.5">
          <div className="flex gap-1 flex-wrap">
            <button
              onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-xl font-black text-[10px] uppercase tracking-wide transition-all ${
                statusFilter === 'all'
                  ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              All ({statusCounts.all})
            </button>
            {Object.entries(statusConfig).map(([status, cfg]) => (
              statusCounts[status] > 0 && (
                <button
                  key={status}
                  onClick={() => { setStatusFilter(status); setCurrentPage(1); }}
                  className={`px-4 py-2 rounded-xl font-black text-[10px] uppercase tracking-wide transition-all flex items-center gap-1.5 ${
                    statusFilter === status
                      ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${statusFilter === status ? 'bg-white' : cfg.dot}`} />
                  {cfg.label} ({statusCounts[status]})
                </button>
              )
            ))}
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          {/* Search */}
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by permit number, turbine, scope, project..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-300 transition-colors"
                />
              </div>
              <select
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="h-10 px-3 text-xs font-bold bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-300"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('work_permit_number')}
                      className="flex items-center text-[10px] font-black text-slate-500 uppercase tracking-wider hover:text-slate-700"
                    >
                      Permit No. {renderSortIndicator('work_permit_number')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('status')}
                      className="flex items-center text-[10px] font-black text-slate-500 uppercase tracking-wider hover:text-slate-700"
                    >
                      Status {renderSortIndicator('status')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Project</span>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Turbine</span>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Team Leader</span>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('planned_end')}
                      className="flex items-center text-[10px] font-black text-slate-500 uppercase tracking-wider hover:text-slate-700"
                    >
                      Date Range {renderSortIndicator('planned_end')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Work Scope</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedWorkPermits.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                          <ClipboardList className="h-6 w-6 text-slate-400" />
                        </div>
                        <p className="text-sm font-black text-slate-900">No work permits found</p>
                        <p className="text-xs text-slate-500">
                          {searchTerm || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Create your first work permit'}
                        </p>
                        {!searchTerm && statusFilter === 'all' && projects.length > 0 && (
                          <button
                            onClick={openCreateModal}
                            className="mt-2 text-[10px] font-black text-emerald-600 hover:text-emerald-700 uppercase tracking-wide"
                          >
                            Create Work Permit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedWorkPermits.map((wp) => {
                    const status = statusConfig[wp.status] || statusConfig.draft;
                    return (
                      <tr key={wp.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <span className="text-sm font-black text-slate-900">{wp.work_permit_number || '-'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[9px] font-black rounded-lg uppercase ${status.bg} ${status.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                            {status.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-600">{wp.project_code || '-'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-600">{wp.turbine_name || '-'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <Users className="h-3 w-3 text-slate-400" />
                            <span className="text-sm text-slate-600">{getTeamLeaderName(wp)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs text-slate-500">
                            <div>{formatDate(wp.planned_start)}</div>
                            <div className="text-slate-400">to {formatDate(wp.planned_end)}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-600 truncate max-w-[200px] block">{wp.work_scope_name || '-'}</span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {sortedWorkPermits.length > 0 && (
            <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between">
              <div className="text-xs text-slate-500 font-medium">
                Showing {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, sortedWorkPermits.length)} of {sortedWorkPermits.length}
              </div>
              <div className="flex items-center gap-1.5">
                {currentPage > 1 && (
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className="h-8 px-3 flex items-center gap-1 text-[10px] font-black uppercase tracking-wide text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    Prev
                  </button>
                )}
                {getVisiblePages().map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`h-8 w-8 flex items-center justify-center text-[10px] font-black rounded-lg transition-colors ${
                      page === currentPage
                        ? 'bg-orange-500 text-white'
                        : 'text-slate-600 bg-slate-100 hover:bg-slate-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                {currentPage < totalPages && (
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className="h-8 px-3 flex items-center gap-1 text-[10px] font-black uppercase tracking-wide text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                  >
                    Next
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Create Work Permit Modal */}
        {showCreateModal && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => setShowCreateModal(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <ClipboardList className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">Create Work Permit</h3>
                    <p className="text-[10px] text-slate-500 font-medium">Submit work permit request to OEM</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="w-8 h-8 rounded-xl hover:bg-slate-100 flex items-center justify-center transition-colors"
                >
                  <X className="h-4 w-4 text-slate-500" />
                </button>
              </div>

              {/* Two Column Layout */}
              <div className="flex-1 flex overflow-hidden">
                {/* Left Column - Form */}
                <div className="w-1/2 border-r border-slate-200 p-5 space-y-4 overflow-y-auto">
                  <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-wide">Work Permit Details</h4>

                  <div>
                    <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wide mb-1.5">
                      OEM Project *
                    </label>
                    <select
                      value={formData.project}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, project: e.target.value, supervisor: '', preparer: '', work_scope: '', work_scope_category: '', teamSearch: '' }));
                        setSelectedTeamMembers(new Set());
                        fetchProjectTechnicians(e.target.value);
                        fetchWorkScopes(e.target.value);
                      }}
                      className="w-full h-10 px-3 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm"
                    >
                      <option value="">Select project...</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.code} - {p.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wide mb-1.5">
                      Turbine Name/ID
                    </label>
                    <input
                      type="text"
                      value={formData.turbine_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, turbine_name: e.target.value }))}
                      placeholder="e.g., WTG-01"
                      className="w-full h-10 px-3 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wide mb-1.5">
                      Work Scope *
                    </label>
                    <select
                      value={formData.work_scope}
                      onChange={(e) => setFormData(prev => ({ ...prev, work_scope: e.target.value, work_scope_category: '' }))}
                      disabled={!formData.project || loadingWorkScopes}
                      className="w-full h-10 px-3 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm disabled:opacity-50"
                    >
                      <option value="">
                        {loadingWorkScopes ? 'Loading...' : !formData.project ? 'Select project first...' : 'Select work scope...'}
                      </option>
                      {workScopes.map(ws => (
                        <option key={ws.id} value={ws.id}>
                          {ws.code ? `${ws.code} - ` : ''}{ws.name}
                        </option>
                      ))}
                    </select>
                    {workScopes.length === 0 && formData.project && !loadingWorkScopes && (
                      <p className="text-[10px] text-amber-600 mt-1">No work scopes defined for this project's OEM</p>
                    )}
                  </div>

                  {/* Work Scope Category (if work scope has categories) */}
                  {formData.work_scope && workScopes.find(ws => ws.id === parseInt(formData.work_scope))?.categories?.length > 0 && (
                    <div>
                      <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wide mb-1.5">
                        Category
                      </label>
                      <select
                        value={formData.work_scope_category}
                        onChange={(e) => setFormData(prev => ({ ...prev, work_scope_category: e.target.value }))}
                        className="w-full h-10 px-3 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm"
                      >
                        <option value="">Select category (optional)...</option>
                        {workScopes.find(ws => ws.id === parseInt(formData.work_scope))?.categories?.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wide mb-1.5">
                      Work Description
                    </label>
                    <textarea
                      value={formData.work_description}
                      onChange={(e) => setFormData(prev => ({ ...prev, work_description: e.target.value }))}
                      placeholder="Detailed description of work to be performed..."
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wide mb-1.5">
                        Start Date/Time *
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.planned_start}
                        onChange={(e) => setFormData(prev => ({ ...prev, planned_start: e.target.value }))}
                        className="w-full h-10 px-3 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wide mb-1.5">
                        End Date/Time *
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.planned_end}
                        onChange={(e) => setFormData(prev => ({ ...prev, planned_end: e.target.value }))}
                        className="w-full h-10 px-3 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm"
                      />
                    </div>
                  </div>

                  {formData.project && (
                    <div className="pt-3 border-t border-slate-200">
                      <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-wide mb-3">Team Leadership</h4>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wide mb-1.5">
                            Supervisor (Team Leader) *
                          </label>
                          <select
                            value={formData.supervisor}
                            onChange={(e) => setFormData(prev => ({ ...prev, supervisor: e.target.value }))}
                            disabled={loadingTechnicians}
                            className="w-full h-10 px-3 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm disabled:opacity-50"
                          >
                            <option value="">{loadingTechnicians ? 'Loading...' : 'Select supervisor...'}</option>
                            {projectTechnicians.map(t => (
                              <option key={t.id} value={t.id}>
                                {t.name} ({t.authorization_level_code})
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wide mb-1.5">
                            Preparer (Optional)
                          </label>
                          <select
                            value={formData.preparer}
                            onChange={(e) => setFormData(prev => ({ ...prev, preparer: e.target.value }))}
                            disabled={loadingTechnicians}
                            className="w-full h-10 px-3 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm disabled:opacity-50"
                          >
                            <option value="">{loadingTechnicians ? 'Loading...' : 'Same as supervisor'}</option>
                            {projectTechnicians.map(t => (
                              <option key={t.id} value={t.id}>
                                {t.name} ({t.authorization_level_code})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.project && (
                    <div className="flex items-start gap-2 p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                      <AlertCircle className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <p className="text-[10px] text-emerald-800 font-medium">
                        Work permit will be sent to {projects.find(p => p.id === parseInt(formData.project))?.oem_company_name || 'OEM'} for approval.
                      </p>
                    </div>
                  )}
                </div>

                {/* Right Column - Team Members Selection */}
                <div className="w-1/2 p-5 overflow-y-auto bg-slate-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-wide">
                      Team Members ({selectedTeamMembers.size} selected)
                    </h4>
                    {projectTechnicians.length > 0 && (
                      <button
                        onClick={() => {
                          const eligibleTechs = projectTechnicians.filter(
                            t => t.id !== parseInt(formData.supervisor) && t.id !== parseInt(formData.preparer)
                          );
                          if (selectedTeamMembers.size === eligibleTechs.length) {
                            setSelectedTeamMembers(new Set());
                          } else {
                            setSelectedTeamMembers(new Set(eligibleTechs.map(t => t.id)));
                          }
                        }}
                        className="text-[10px] font-black text-emerald-600 hover:text-emerald-700 uppercase tracking-wide"
                      >
                        {selectedTeamMembers.size === projectTechnicians.filter(t => t.id !== parseInt(formData.supervisor) && t.id !== parseInt(formData.preparer)).length ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>

                  {/* Search Team Members */}
                  {formData.project && projectTechnicians.length > 0 && (
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search team members..."
                        value={formData.teamSearch || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, teamSearch: e.target.value }))}
                        className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-300 transition-colors"
                      />
                    </div>
                  )}

                  {!formData.project ? (
                    <div className="text-center py-12 text-slate-500">
                      <FolderKanban className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p className="text-xs font-medium">Select a project first</p>
                      <p className="text-[10px] text-slate-400 mt-1">Team members will be loaded from the selected project</p>
                    </div>
                  ) : loadingTechnicians ? (
                    <div className="text-center py-12 text-slate-500">
                      <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                      <p className="text-xs font-medium">Loading technicians...</p>
                    </div>
                  ) : projectTechnicians.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p className="text-xs font-medium">No technicians found for this project</p>
                      <p className="text-[10px] text-slate-400 mt-1">Make sure technicians are assigned or have authorizations</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {projectTechnicians
                        .filter(t => {
                          // Exclude supervisor and preparer
                          if (t.id === parseInt(formData.supervisor) || t.id === parseInt(formData.preparer)) return false;
                          // Search filter
                          if (formData.teamSearch) {
                            const search = formData.teamSearch.toLowerCase();
                            return t.name?.toLowerCase().includes(search) ||
                                   t.authorization_level_code?.toLowerCase().includes(search) ||
                                   t.company_name?.toLowerCase().includes(search);
                          }
                          return true;
                        })
                        .sort((a, b) => {
                          // Sort by has_authorization - authorized technicians first
                          if (a.has_authorization && !b.has_authorization) return -1;
                          if (!a.has_authorization && b.has_authorization) return 1;
                          return a.name.localeCompare(b.name);
                        })
                        .map(tech => {
                          const isSelected = selectedTeamMembers.has(tech.id);
                          return (
                            <div
                              key={tech.id}
                              onClick={() => toggleTeamMember(tech.id)}
                              className={`rounded-xl border p-3 cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-emerald-300 bg-emerald-50'
                                  : 'border-slate-200 bg-white hover:border-emerald-200 hover:bg-emerald-50/50'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                                  isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'
                                }`}>
                                  {isSelected && <CheckCircle className="h-3.5 w-3.5 text-white" />}
                                </div>
                                {tech.profile_picture ? (
                                  <img
                                    src={getImageUrl(tech.profile_picture)}
                                    alt={tech.name}
                                    className="w-9 h-9 rounded-xl object-cover border-2 border-emerald-100"
                                  />
                                ) : (
                                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white text-sm font-black">
                                    {tech.name?.charAt(0)?.toUpperCase() || 'U'}
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-black text-slate-900">{tech.name}</p>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span
                                      className="inline-block px-2 py-0.5 text-[9px] font-black rounded-lg text-white uppercase"
                                      style={{ backgroundColor: tech.authorization_level_color || '#10B981' }}
                                    >
                                      {tech.authorization_level_code}
                                    </span>
                                    {tech.has_authorization && tech.authorization_valid_until && (
                                      <span className="text-[11px] text-slate-500 font-medium">
                                        Expires on {new Date(tech.authorization_valid_until).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}

                      {/* Show supervisor/preparer if selected */}
                      {(formData.supervisor || formData.preparer) && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <p className="text-[10px] font-black text-slate-500 uppercase tracking-wide mb-2">
                            Team Leadership (auto-included)
                          </p>
                          <div className="space-y-2">
                            {formData.supervisor && (
                              <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                                <Briefcase className="h-3.5 w-3.5 text-amber-600" />
                                <span className="text-xs font-bold text-amber-800">
                                  Supervisor: {projectTechnicians.find(t => t.id === parseInt(formData.supervisor))?.name}
                                </span>
                              </div>
                            )}
                            {formData.preparer && formData.preparer !== formData.supervisor && (
                              <div className="flex items-center gap-2 p-2 bg-violet-50 rounded-lg border border-violet-200">
                                <Users className="h-3.5 w-3.5 text-violet-600" />
                                <span className="text-xs font-bold text-violet-800">
                                  Preparer: {projectTechnicians.find(t => t.id === parseInt(formData.preparer))?.name}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 py-4 border-t border-slate-100 flex justify-between bg-slate-50 flex-shrink-0">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="h-10 px-4 bg-slate-200 text-slate-700 rounded-xl hover:bg-slate-300 font-black text-[10px] uppercase tracking-wide transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={createLoading || !formData.project || !formData.work_scope || !formData.supervisor || !formData.planned_start || !formData.planned_end}
                  className="h-10 px-4 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-black text-[10px] uppercase tracking-wide transition-colors shadow-lg shadow-emerald-500/20"
                >
                  {createLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Create Work Permit
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SuspendedCompanyOverlay>
  );
};

export default WindCompanyWorkPermits;
